import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Chip from '@mui/joy/Chip'
import Radio from '@mui/joy/Radio'
import RadioGroup from '@mui/joy/RadioGroup'
import Sheet from '@mui/joy/Sheet'
import Skeleton from '@mui/joy/Skeleton'
import Stack from '@mui/joy/Stack'
import Typography from '@mui/joy/Typography'
import { type ChangeEvent, type FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CreditsIcon from 'ASSETS/img/credits.svg?react'
import { type CreditPackageResponse } from 'TYPES/index'

import { getBestValueDiscount } from './getBestValueDiscount'

interface CreditPackagesPickerProps {
  packages: CreditPackageResponse[]
  isLoading?: boolean
  onConfirm: (packageId: string) => void
  onCancel: () => void
}

const SX_PICKER = { p: 2.5, width: { xs: '100%', md: 520 } } as const
const SX_PACKAGES = {
  mt: 2.5,
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(128px, 1fr))' },
  gap: 1.5,
} as const
const SX_PACKAGE = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  p: 2,
  borderRadius: 'md',
  border: '1px solid',
  borderColor: 'neutral.outlinedBorder',
  bgcolor: 'background.surface',
  cursor: 'pointer',
  transition: 'border-color 120ms, background-color 120ms, box-shadow 120ms',
  '&:hover': {
    borderColor: 'neutral.outlinedHoverBorder',
    bgcolor: 'background.level1',
  },
  '&:has(input:checked)': {
    borderColor: 'primary.500',
    bgcolor: 'primary.softBg',
    boxShadow: 'inset 0 0 0 1px var(--joy-palette-primary-500)',
  },
  '&:has(input:focus-visible)': {
    outline: '2px solid',
    outlineColor: 'focusVisible',
    outlineOffset: 2,
  },
} as const
const SX_RADIO_ACTION = {
  borderRadius: 'md',
  '&:hover': { bgcolor: 'transparent' },
  '&.Mui-focusVisible': { outline: 'none' },
} as const
const SX_CREDITS = { alignItems: 'center', gap: 0.75 } as const
const SX_PRICE = { mt: 'auto', pt: 1.5, gap: 0.75, alignItems: 'flex-start' } as const
const SX_FOOTER = { justifyContent: 'flex-end', gap: 1.5, mt: 3 } as const

const SKELETON_PACKAGES = 2
const SX_PACKAGE_SKELETON = { position: 'relative', height: 120, borderRadius: 'md' } as const

const formatPrice = (price = 0): string => `$${price.toFixed(2)}`

const CreditPackagesPicker: FC<CreditPackagesPickerProps> = ({
  packages,
  isLoading = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const bestValue = useMemo(() => getBestValueDiscount(packages), [packages])

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSelectedId(event.target.value)
  }

  const handleConfirm = (): void => {
    if (selectedId !== null) onConfirm(selectedId)
  }

  return (
    <Box sx={SX_PICKER}>
      <Typography level="title-lg">{t('settings.credits.picker.title')}</Typography>
      <Typography level="body-sm" mt={1}>
        {t('settings.credits.picker.description')}
      </Typography>

      <RadioGroup value={selectedId} onChange={handleChange} sx={SX_PACKAGES}>
        {isLoading &&
          Array.from({ length: SKELETON_PACKAGES }, (_, index) => (
            <Skeleton
              key={index}
              data-testid="credit-package-skeleton"
              variant="rectangular"
              sx={SX_PACKAGE_SKELETON}
            />
          ))}
        {packages.map(({ id, credits = 0, price, description }) => (
          <Sheet key={id} data-testid={`credit-package-${id ?? ''}`} sx={SX_PACKAGE}>
            <Radio
              overlay
              disableIcon
              variant="plain"
              value={id}
              slotProps={{ action: { sx: SX_RADIO_ACTION } }}
              label={
                <Stack direction="row" sx={SX_CREDITS}>
                  <CreditsIcon aria-hidden="true" width={20} height={20} />
                  <Typography level="title-lg">{credits.toLocaleString('en-US')}</Typography>
                </Stack>
              }
            />
            <Typography level="body-xs">{description}</Typography>
            <Stack sx={SX_PRICE}>
              {bestValue !== undefined && id === bestValue.packageId && (
                <Chip size="sm" variant="soft" color={selectedId === id ? 'primary' : 'neutral'}>
                  {t('settings.credits.picker.save', { percent: bestValue.percent })}
                </Chip>
              )}
              <Typography level="title-md">{formatPrice(price)}</Typography>
            </Stack>
          </Sheet>
        ))}
      </RadioGroup>

      <Stack direction="row" sx={SX_FOOTER}>
        <Button variant="outlined" color="neutral" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button disabled={selectedId === null} onClick={handleConfirm}>
          {t('settings.credits.picker.confirm')}
        </Button>
      </Stack>
    </Box>
  )
}

export default CreditPackagesPicker
